import { useMsal } from '@azure/msal-react';
import { useEffect, useState, useMemo } from 'react';
import userRegistryApi from '../api/userRegistryApi';
import { isUserAdmin } from '../constants/constants';
import sessionManager from '../utils/sessionManager';

/**
 * Custom hook to handle user authentication and automatic registration
 * Automatically registers users on successful login and tracks login history
 * Uses existing associations container with record type filtering
 */
export const useUserAuth = () => {
  const { accounts } = useMsal();
  const [currentUser, setCurrentUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  const account = accounts?.[0];
  const isAuthenticated = accounts.length > 0;

  useEffect(() => {
    const handleUserAuth = async () => {
      // Prevent redundant calls if already loading or already have user
      if (currentUser && !authError) return;

      // In development mode or local testing, mock Mario Vaccari as authenticated user
      if (import.meta.env.DEV || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname.includes('wsl.localhost')) {
        console.log('🚀 Local mode: Setting Mario Vaccari as authenticated user');
        setCurrentUser({
          email: 'mario.vaccari@condoshield.org',
          name: 'Mario Vaccari',
          isAdmin: true,
          account: {
            username: 'mario.vaccari@condoshield.org',
            name: 'Mario Vaccari',
          },
          isDevelopmentMode: true,
        });
        setUserLoading(false);
        return;
      }

      if (!isAuthenticated || !account) {
        setCurrentUser(null);
        setUserLoading(false);
        return;
      }

      try {
        setUserLoading(true);
        setAuthError(null);

        const userEmail = account.username;
        const userName = account.name;

        console.log('🔐 User Authentication:', {
          userEmail,
          userName,
          isAuthenticated,
        });

        if (!userEmail || !userName) {
          throw new Error('Invalid user account information');
        }

        // Initialize session management for this user
        sessionManager.initializeSession(userEmail);

        // Check if user is admin based on email
        const userIsAdmin = isUserAdmin(userEmail);
        console.log('👑 Admin status:', { userEmail, userIsAdmin });

        // Try to register/update user in the registry (using associations container)
        try {
          console.log('📝 Registering user in associations container...');
          const userData = await userRegistryApi.registerOrUpdateUser({
            name: userName,
            email: userEmail,
            isAdmin: userIsAdmin,
          });

          console.log('✅ User registration successful');

          setCurrentUser({
            ...userData,
            email: userEmail,
            name: userName,
            isAdmin: userIsAdmin,
            account, // Keep reference to MSAL account
          });
        } catch (registryError) {
          console.log('⚠️ User registry unavailable, using basic auth:', registryError.message);

          // Set basic user info even if registry fails
          setCurrentUser({
            email: userEmail,
            name: userName,
            isAdmin: userIsAdmin,
            account,
            registryError: true,
          });
        }
      } catch (error) {
        console.error('❌ Authentication error:', error);
        setAuthError(error.message);

        // Fallback to basic user info
        setCurrentUser({
          email: account.username,
          name: account.name,
          isAdmin: isUserAdmin(account.username),
          account,
          registryError: true,
        });
      } finally {
        setUserLoading(false);
      }
    };

    handleUserAuth();
  }, [isAuthenticated, account?.username, account?.name]);

  return useMemo(() => ({
    currentUser,
    isAuthenticated: import.meta.env.DEV ? true : isAuthenticated,
    userLoading,
    authError,
    isAdmin: currentUser?.isAdmin || false,
    account: currentUser?.account || account,
  }), [currentUser, isAuthenticated, userLoading, authError, account]);
};

export default useUserAuth;
